import {
  Check,
  X,
  Star,
  Zap,
  Crown,
  Loader2,
  AlertCircle,
  Package,
  XIcon,
  Mail,
  Phone,
  Building2,
  User
} from 'lucide-react';
import { useState } from 'react';

// import Button from '../components/Common/Button';
import PaymentCoupns from '../components/ReUsableComponents/PaymentCoupns';
import { Item, Plan } from '../redux/Slices/PaymentsSlice';
import { useAppSelector } from '../redux/store/hooks';
import {
  useCreatePlanMutation,
  useGetPlansQuery,
  useValidateCouponMutation
} from '../services/payments';
import { cn } from '../utils/tailwindMerge';

type CouponData = {
  discountPercentage: number;
  minimumPurchasedOrder: number;
  maxCappedAmount: number;
  expiry: string;
  couponCode: string;
  id: number;
};

const PricingPlans = () => {
  const { userId } = useAppSelector(state => state.root.auth);
  const { paymentsData } = useAppSelector(state => state.root.payments);
  const [createPlan, { isLoading: isCreatingPlan }] = useCreatePlanMutation();
  const [showProOverlay, setShowProOverlay] = useState(false);
  const [showContactOverlay, setShowContactOverlay] = useState(false);
  const [selectedModules, setSelectedModules] = useState<number[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showCoupnsOverlay, setShowCoupnsOverlay] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponData | null>(null);
  const [couponError, setCouponError] = useState('');
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [validateCoupon] = useValidateCouponMutation();

  const [couponData, setCoupanData] = useState<CouponData>();

  const calculateSubtotal = () => {
    const modulePricings = selectedPlan?.moduleBasedPricing?.filter(m =>
      selectedModules.includes(m.moduleCode)
    );
    let cost = modulePricings?.reduce(
      (total, module) => total + module.price,
      0
    );
    if (!selectedPlan?.moduleBasedPricing) {
      cost = selectedPlan?.priceDTO?.enableOfferPrice
        ? selectedPlan?.priceDTO?.offerAmount
        : selectedPlan?.priceDTO?.originalAmount;
    }
    return cost ?? 0;
  };

  const handleContinueWithCoupon = async () => {
    const body = {
      code: couponCode,
      minimumPurchasedOrder: calculateSubtotal(),
      dashboards: selectedModules
    };
    try {
      const couponData = await validateCoupon(body).unwrap();
      setCoupanData(couponData?.data);
      validateAndApplyCoupon(
        couponData?.data?.couponCode ?? '',
        couponData?.data
      );

      setShowCoupnsOverlay(false);
    } catch (error) {
      setCouponError(
        (error as { data?: { message: string } })?.data?.message ?? ''
      );
      console.log(error, 'error67');
    }
  };

  const validateAndApplyCoupon = (code: string, couponData: CouponData) => {
    setCouponError('');

    // Trim and convert to uppercase for comparison
    const trimmedCode = code.trim().toUpperCase();

    if (!trimmedCode) {
      setCouponError('Please enter a coupon code');
      return;
    }

    // Check if coupon code matches
    if (trimmedCode !== couponData.couponCode.toUpperCase()) {
      setCouponError('Invalid coupon code');
      return;
    }

    // Check expiry
    const expiryDate = new Date(couponData.expiry);
    const currentDate = new Date();
    if (currentDate > expiryDate) {
      setCouponError('This coupon has expired');
      return;
    }

    // Check minimum order amount
    const subtotal = calculateSubtotal();
    if (subtotal < couponData.minimumPurchasedOrder) {
      setCouponError(
        `Minimum order amount of ₹${couponData.minimumPurchasedOrder} required`
      );
      return;
    }

    // Apply coupon
    setAppliedCoupon(couponData);
    setCouponCode(trimmedCode);
  };

  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;

    const subtotal = calculateSubtotal();
    const calculatedDiscount =
      (subtotal * appliedCoupon?.discountPercentage) / 100;

    // Use maxCappedAmount if it exists, otherwise use calculated discount
    if (appliedCoupon?.maxCappedAmount) {
      return Math.min(calculatedDiscount, appliedCoupon?.maxCappedAmount);
    }

    return calculatedDiscount;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    return subtotal - discount;
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  // Get query state for proper error and loading handling
  const { data, isLoading, isError, error, refetch, isFetching } =
    useGetPlansQuery({}, { refetchOnMountOrArgChange: true });

  const getPlanIcon = (category: string) => {
    switch (category) {
      case 'basic':
        return <Star className='h-6 w-6' />;
      case 'pro':
        return <Zap className='h-6 w-6' />;
      case 'premium':
        return <Crown className='h-6 w-6' />;
      default:
        return <Star className='h-6 w-6' />;
    }
  };

  const handleClickBuyNow = async (plan: Plan) => {
    if (plan?.name?.toLowerCase() === 'free') return;
    if (plan?.name?.toLowerCase() === 'module based') {
      setSelectedPlan(plan);
      setShowProOverlay(true);
      setSelectedModules([]);
      return;
    }

    if (plan?.name?.toLowerCase() === 'premium') {
      setSelectedPlan(plan);
      setShowProOverlay(true);
      setSelectedModules(plan?.priceDTO?.providedModules || []);
      return;
    }

    if (plan?.name?.toLowerCase() === 'pro') {
      setSelectedPlan(plan);
      setShowProOverlay(true);
      setSelectedModules(plan?.priceDTO?.providedModules || []);
      return;
    }

    // Handle Premium Plan - Show contact overlay
    if (plan?.name?.toLowerCase() === 'enterprise') {
      setSelectedPlan(plan);
      setShowContactOverlay(true);
      return;
    }

    if (plan?.name?.toLowerCase() === 'ultimate') {
      setSelectedPlan(plan);
    }
    const selectedModules = plan?.priceDTO?.providedModules || [];
    const finalAmount = plan?.priceDTO?.enableOfferPrice
      ? plan?.priceDTO?.offerAmount
      : plan?.priceDTO?.originalAmount || 0;

    // Handle other plans - Original logic
    if (!userId || !plan?.id) return;
    try {
      const res = await createPlan({
        userId: userId || '',
        planId: plan.id,
        selectedModules: plan?.priceDTO?.providedModules || [],
        couponApplied: false,
        finalAmount
      }).unwrap();
      if (res.status === 'success') {
        const myWindow = window.open('', '_self');
        if (myWindow) {
          myWindow.document.open();
          myWindow.document.write(res.data.paymentUrl);
          myWindow.document.close();
        }
      }
    } catch (err) {
      console.log(err, 'err');
    }
  };

  const handleModuleToggle = (moduleId: number) => {
    setSelectedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleProceedPayment = async () => {
    if (selectedModules.length === 0) return;
    const finalAmount = calculateTotal();
    try {
      const res = await createPlan({
        userId: userId || '',
        planId: selectedPlan?.id || '',
        selectedModules,
        finalAmount,
        durationMonths: 1,
        couponApplied: !!appliedCoupon,
        // code: appliedCoupon && couponCode,
        couponCodeId: appliedCoupon?.id
      }).unwrap();
      if (res.status === 'success') {
        const myWindow = window.open('', '_self');
        if (myWindow) {
          myWindow.document.open();
          myWindow.document.write(res.data.paymentUrl);
          myWindow.document.close();
        }
      }
      setShowProOverlay(false);
    } catch (err) {
      console.log(err, 'err');
    }
  };

  const renderBasicPlan = (plan: Plan) => {
    const getPlanIconByName = (name: string) => {
      const lowerName = name?.toLowerCase();
      if (lowerName === 'pro') return <Zap className='h-6 w-6 text-white' />;
      if (lowerName === 'premium')
        return <Crown className='h-6 w-6 text-white' />;
      return <Star className='h-6 w-6 text-white' />;
    };
    const isOfferPriceAvailable = plan?.priceDTO?.enableOfferPrice;

    return (
      <div
        key={plan.id}
        className='flex flex-col rounded-2xl border-2 bg-white p-8 shadow-lg transition-all duration-300 hover:shadow-xl'
        style={{
          borderColor: plan?.colors?.primary
        }}
      >
        {/* Header Section */}
        <div className='mb-6 flex items-center space-x-3'>
          {/* <div
            style={{
              backgroundColor: plan?.colors?.primary
            }}
            className='rounded-xl p-3'
          >
            {getPlanIconByName(plan?.name)}
          </div> */}
          <h3
            className='text-3xl font-bold text-gray-900'
            style={{
              color: plan?.colors?.primary
            }}
          >
            {plan?.name}
            <p className='my-3 text-base text-gray-600'>{plan?.tagline}</p>
          </h3>
        </div>

        {/* Price Section */}
        <div className='mb-6'>
          {isOfferPriceAvailable && (
            <div className='mb-2 flex items-baseline gap-3'>
              <span className='text-xl text-gray-400 line-through'>
                ₹{plan?.priceDTO?.originalAmount}
              </span>
              {/* <span className='rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-600'>
                {(plan?.name === 'Pro' || plan?.name === 'Premium') &&
                  'SAVE 50%'}
              </span> */}
            </div>
          )}
          <div className='mb-2 text-2xl font-bold text-gray-900'>
            {plan?.priceDTO?.displayText}
          </div>
          <p>{plan?.description?.text}</p>
        </div>

        {/* Features List - This will grow to fill available space */}
        <ul className='mb-8 flex-grow space-y-3'>
          <li className='font-semibold'>{plan?.features?.title}</li>
          {plan?.features?.items
            ?.toSorted((a: Item, b: Item) => a.sortOrder - b.sortOrder)
            ?.map((service: Item, index: number) => (
              <li
                key={service?.item || index}
                className='flex items-start space-x-3'
              >
                <div
                  style={{
                    backgroundColor: service?.availableForThisPlan
                      ? plan?.colors?.primary
                      : '#FA5F55'
                  }}
                  className='flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full'
                >
                  {service?.availableForThisPlan ? (
                    <Check className='h-3 w-3 text-white' />
                  ) : (
                    <X className='h-3 w-3 text-white' />
                  )}
                </div>
                <span
                  className={`text-sm ${service?.availableForThisPlan ? 'font-medium text-gray-900' : 'text-gray-500'}`}
                >
                  {service?.item}
                </span>
              </li>
            ))}
        </ul>

        {/* Button - Always at the bottom */}
        <button
          style={{
            backgroundColor: plan?.button?.backgroundColor
          }}
          onClick={() => handleClickBuyNow(plan)}
          disabled={isCreatingPlan || plan?.button?.text === 'Already Started'}
          className={cn(
            'w-full rounded-xl px-6 py-3 text-base font-semibold text-white transition-all duration-200 hover:opacity-90 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60',
            isCreatingPlan && 'cursor-wait'
          )}
        >
          {isCreatingPlan ? (
            <div className='flex items-center justify-center space-x-2'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span>Processing...</span>
            </div>
          ) : (
            plan?.button?.text
          )}
        </button>
      </div>
    );
  };

  // Loading State
  const renderLoader = () => (
    <div className='flex min-h-[400px] items-center justify-center'>
      <div className='text-center'>
        <Loader2 className='mx-auto mb-4 h-12 w-12 animate-spin text-blue-600' />
        <h3 className='mb-2 text-xl font-semibold text-gray-900'>
          Loading Plans
        </h3>
        <p className='text-gray-600'>
          Please wait while we fetch the pricing plans...
        </p>
      </div>
    </div>
  );

  // Error State
  const renderError = () => (
    <div className='flex min-h-[400px] items-center justify-center'>
      <div className='mx-auto max-w-md text-center'>
        <AlertCircle className='mx-auto mb-4 h-16 w-16 text-red-500' />
        <h3 className='mb-2 text-xl font-semibold text-gray-900'>
          Oops! Something went wrong
        </h3>
        <p className='mb-6 text-gray-600'>
          {error &&
            'We encountered an issue while loading the pricing plans. Please try again.'}
        </p>
        <button
          onClick={() => refetch()}
          className='inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors duration-200 hover:bg-blue-700'
        >
          <Loader2
            className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')}
          />
          {isFetching ? 'Retrying...' : 'Try Again'}
        </button>
      </div>
    </div>
  );

  // Empty State
  const renderEmptyState = () => (
    <div className='flex min-h-[400px] items-center justify-center'>
      <div className='mx-auto max-w-md text-center'>
        <Package className='mx-auto mb-4 h-16 w-16 text-gray-400' />
        <h3 className='mb-2 text-xl font-semibold text-gray-900'>
          No Plans Available
        </h3>
        <p className='mb-6 text-gray-600'>
          We don&apos;t have any pricing plans available at the moment. Please
          check back later or contact support.
        </p>
        <button
          onClick={() => refetch()}
          className='inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors duration-200 hover:bg-blue-700'
        >
          <Loader2
            className={cn('mr-2 h-4 w-4', isFetching && 'animate-spin')}
          />
          {isFetching ? 'Checking...' : 'Refresh'}
        </button>
      </div>
    </div>
  );

  // Main render logic with conditional rendering
  const renderContent = () => {
    if (isLoading) {
      return renderLoader();
    }

    if (isError) {
      return renderError();
    }

    if (!paymentsData || paymentsData.length === 0) {
      return renderEmptyState();
    }

    return (
      <div className='mb-12 grid grid-cols-1 gap-8 lg:grid-cols-3 2xl:grid-cols-4'>
        {paymentsData
          ?.slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(plan => renderBasicPlan(plan))}{' '}
      </div>
    );
  };

  return (
    <div className='min-h-screen bg-white px-4 py-16'>
      <div className='mx-auto max-w-7xl'>
        <div className='mb-16 text-center'>
          <h1 className='mb-4 text-5xl font-bold text-gray-900'>
            Choose Your Perfect Plan
          </h1>
          <p className='mx-auto max-w-3xl text-xl text-gray-600'>
            Unlock your potential with our comprehensive tools and services.
            From free trials to enterprise solutions, we have the perfect plan
            to help you succeed.
          </p>
        </div>

        {renderContent()}
      </div>

      {/* Pro Plan Overlay */}
      {showProOverlay && selectedPlan && (
        <div className='fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-50'>
          <div
            className='h-full w-full max-w-md overflow-y-auto bg-white shadow-2xl'
            style={{ animation: 'slideIn 0.3s ease-out' }}
          >
            {/* Header */}
            <div
              className='sticky top-0 z-10 flex items-center justify-between border-b bg-white p-6'
              style={{ borderColor: selectedPlan?.colors?.secondary }}
            >
              <div className='flex items-center space-x-3'>
                <div
                  style={{
                    backgroundColor: selectedPlan?.colors?.primary
                  }}
                  className='rounded-lg p-2'
                >
                  <Zap className='h-5 w-5 text-white' />
                </div>
                <div>
                  <h2 className='text-2xl font-bold text-gray-900'>
                    {selectedPlan?.name}
                  </h2>
                  {selectedPlan?.moduleBasedPricing && (
                    <p className='text-sm text-gray-600'>
                      Select modules to continue
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setCouponCode('');
                  setShowProOverlay(false);
                }}
                className='rounded-lg p-2 transition-colors hover:bg-gray-100'
              >
                <XIcon className='h-6 w-6 text-gray-600' />
              </button>
            </div>

            {/* Content */}
            <div className='p-6'>
              {/* Plan Details */}
              <div
                className='mb-6 rounded-xl p-4'
                style={{
                  backgroundColor: selectedPlan?.colors?.bgSecondary
                }}
              >
                <h3 className='mb-2 text-lg font-semibold text-gray-900'>
                  Plan Details
                </h3>
                <p className='mb-1 text-sm text-gray-700'>
                  {selectedPlan?.description?.text}
                </p>
                {selectedPlan?.moduleBasedPricing && (
                  <div
                    className='mt-3 inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold'
                    style={{
                      backgroundColor: selectedPlan?.colors?.primary,
                      color: 'white'
                    }}
                  >
                    <Zap className='mr-1 h-4 w-4' />
                    {/* {selectedPlan?.moreInfo?.credits} */}
                  </div>
                )}
              </div>

              {/* Module Selection */}
              <div className='mb-6'>
                {selectedPlan?.moduleBasedPricing && (
                  <h3 className='mb-4 text-lg font-semibold text-gray-900'>
                    Select Modules
                  </h3>
                )}
                <div className='space-y-3'>
                  {selectedPlan?.moduleBasedPricing?.map(module => (
                    <label
                      key={module.moduleCode}
                      className='flex cursor-pointer items-center justify-between rounded-lg border-2 p-4 transition-all hover:shadow-md'
                      style={{
                        borderColor: selectedModules.includes(module.moduleCode)
                          ? selectedPlan?.colors?.primary
                          : '#E5E7EB',
                        backgroundColor: selectedModules.includes(
                          module.moduleCode
                        )
                          ? selectedPlan?.colors?.secondary
                          : 'white'
                      }}
                    >
                      <div className='flex items-center space-x-3'>
                        <input
                          type='checkbox'
                          checked={selectedModules.includes(module.moduleCode)}
                          onChange={() => handleModuleToggle(module.moduleCode)}
                          className='h-5 w-5 rounded border-gray-300'
                          style={{
                            accentColor: selectedPlan?.colors?.primary
                          }}
                        />
                        <span className='font-medium text-gray-900'>
                          {module.moduleName}
                        </span>
                      </div>
                      <span className='font-semibold text-gray-900'>
                        ₹{module.price}/mo
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              {/* Coupon Section */}
              <div className='mb-6'>
                <p className='mb-3 font-semibold'>Coupons</p>

                {!appliedCoupon ? (
                  <div>
                    <div className='mb-3 flex gap-2'>
                      <input
                        type='text'
                        placeholder='Enter Coupon Code'
                        value={couponCode ?? ''}
                        onChange={e => {
                          setCouponCode(e.target.value);
                          setCouponError('');
                        }}
                        onKeyPress={e => {
                          if (e.key === 'Enter') {
                            handleContinueWithCoupon();
                          }
                        }}
                        className='flex-1 rounded-md border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                      />
                      <button
                        onClick={() => handleContinueWithCoupon()}
                        className={`rounded-md px-6 py-2 text-white transition-colors ${
                          couponCode
                            ? 'bg-blue-600 hover:bg-blue-700'
                            : 'cursor-not-allowed bg-gray-400'
                        }`}
                        disabled={!couponCode}
                      >
                        Apply
                      </button>
                    </div>

                    {couponError && (
                      <p className='mb-3 text-sm text-red-600'>{couponError}</p>
                    )}
                  </div>
                ) : (
                  <div className='mb-3 rounded-md border border-green-200 bg-green-50 p-3'>
                    <div className='flex items-center justify-between'>
                      <div>
                        <p className='font-semibold text-green-700'>
                          {appliedCoupon?.couponCode} Applied!
                        </p>
                        <p className='text-sm text-green-600'>
                          {appliedCoupon?.discountPercentage}% off
                          {appliedCoupon?.maxCappedAmount &&
                            ` (Max ₹${appliedCoupon?.maxCappedAmount})`}
                        </p>
                      </div>
                      <button
                        onClick={removeCoupon}
                        className='text-sm font-semibold text-red-600 hover:text-red-700'
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setShowCoupnsOverlay(true)}
                  className='w-full rounded-md border border-blue-600 py-2 text-blue-600 transition-colors hover:bg-blue-50'
                >
                  View Available Coupons
                </button>
              </div>

              {/* Price Breakdown */}
              <div className='border-t pt-4'>
                <div className='mb-2 flex justify-between text-gray-600'>
                  <span>Subtotal</span>
                  <span>₹{calculateSubtotal()}</span>
                </div>

                {appliedCoupon && (
                  <div className='mb-2 flex justify-between text-green-600'>
                    <span>Discount ({appliedCoupon?.discountPercentage}%)</span>
                    <span>- ₹{calculateDiscount()}</span>
                  </div>
                )}

                <div className='flex justify-between border-t pt-3 text-xl font-bold'>
                  <span>Total</span>
                  <span>₹{calculateTotal()}</span>
                </div>

                {appliedCoupon && (
                  <p className='mt-2 text-sm text-green-600'>
                    You saved ₹{calculateDiscount()}!
                  </p>
                )}
              </div>

              {/* Features List */}
              <div className='mb-6'>
                <h3 className='mb-4 text-lg font-semibold text-gray-900'>
                  What is Included
                </h3>
                <p>{selectedPlan?.features?.title}</p>
                <ul className='space-y-2'>
                  {selectedPlan?.features?.items
                    ?.filter(s => s.availableForThisPlan)
                    .map((service, index) => (
                      <li
                        key={service?.item}
                        className='flex items-start space-x-2'
                      >
                        <div
                          style={{
                            backgroundColor: selectedPlan?.colors?.primary
                          }}
                          className='mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full'
                        >
                          <Check className='h-3 w-3 text-white' />
                        </div>
                        <span className='text-sm text-gray-700'>
                          {service?.item}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div
              className='sticky bottom-0 border-t bg-white p-6'
              style={{ borderColor: selectedPlan?.colors?.secondary }}
            >
              <div className='mb-4 flex items-center justify-between'>
                <span className='text-lg font-semibold text-gray-900'>
                  Total Amount
                </span>
                <div className='text-right'>
                  <div className='text-2xl font-bold text-gray-900'>
                    ₹{calculateTotal()}
                  </div>
                  <div className='text-sm text-gray-600'>
                    {selectedModules.length}{' '}
                    {selectedModules.length === 1 ? 'module' : 'modules'}{' '}
                    selected
                  </div>
                </div>
              </div>
              <button
                onClick={handleProceedPayment}
                disabled={selectedModules.length === 0 || isCreatingPlan}
                style={{
                  backgroundColor:
                    selectedModules.length === 0
                      ? '#D1D5DB'
                      : selectedPlan?.button?.backgroundColor
                }}
                className='w-full rounded-xl px-6 py-4 font-semibold text-white transition-all duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60'
              >
                {isCreatingPlan ? (
                  <div className='flex items-center justify-center space-x-2'>
                    <Loader2 className='h-5 w-5 animate-spin' />
                    <span>Processing...</span>
                  </div>
                ) : (
                  'Proceed to Payment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* coupnsOverLay */}
      {showCoupnsOverlay && (
        <PaymentCoupns
          amount={calculateTotal()}
          setShowCoupnsOverlay={setShowCoupnsOverlay}
          setCouponCode={setCouponCode}
        />
      )}

      {/* Contact Us Overlay */}
      {showContactOverlay && selectedPlan && (
        <div className='fixed inset-0 z-[9999] flex items-center justify-end bg-black bg-opacity-50'>
          <div
            className='h-full w-full max-w-md overflow-y-auto bg-white shadow-2xl'
            style={{ animation: 'slideIn 0.3s ease-out' }}
          >
            {/* Header */}
            <div
              className='sticky top-0 z-10 flex items-center justify-between border-b bg-white p-6'
              style={{ borderColor: selectedPlan?.colors?.secondary }}
            >
              <div className='flex items-center space-x-3'>
                <div
                  style={{
                    backgroundColor: selectedPlan?.colors?.bgPrimary
                  }}
                  className='rounded-lg p-2'
                >
                  <Crown className='h-5 w-5 text-white' />
                </div>
                <div>
                  <h2 className='text-2xl font-bold text-gray-900'>
                    Contact Us
                  </h2>
                  <p className='text-sm text-gray-600'>Premium Plan Inquiry</p>
                </div>
              </div>
              <button
                onClick={() => setShowContactOverlay(false)}
                className='rounded-lg p-2 transition-colors hover:bg-gray-100'
              >
                <XIcon className='h-6 w-6 text-gray-600' />
              </button>
            </div>

            {/* Content */}
            <div className='p-6'>
              {/* Plan Info */}
              <div
                className='mb-6 rounded-xl p-4'
                style={{
                  backgroundColor: selectedPlan?.colors?.bgSecondary
                }}
              >
                <h3 className='mb-2 text-lg font-semibold text-gray-900'>
                  {selectedPlan?.name}
                </h3>
                <p className='text-sm text-gray-700'>
                  {selectedPlan?.description?.text}
                </p>
              </div>

              {/* Contact Form */}
              <form className='space-y-4'>
                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>
                    Full Name
                  </label>
                  <div className='relative'>
                    <User className='absolute left-3 top-3 h-5 w-5 text-gray-400' />
                    <input
                      type='text'
                      placeholder='Enter your name'
                      className='w-full rounded-lg border-2 border-gray-300 py-3 pl-10 pr-4 focus:border-orange-500 focus:outline-none'
                    />
                  </div>
                </div>

                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>
                    Email Address
                  </label>
                  <div className='relative'>
                    <Mail className='absolute left-3 top-3 h-5 w-5 text-gray-400' />
                    <input
                      type='email'
                      placeholder='your.email@example.com'
                      className='w-full rounded-lg border-2 border-gray-300 py-3 pl-10 pr-4 focus:border-orange-500 focus:outline-none'
                    />
                  </div>
                </div>

                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>
                    Phone Number
                  </label>
                  <div className='relative'>
                    <Phone className='absolute left-3 top-3 h-5 w-5 text-gray-400' />
                    <input
                      type='tel'
                      placeholder='+91 XXXXX XXXXX'
                      className='w-full rounded-lg border-2 border-gray-300 py-3 pl-10 pr-4 focus:border-orange-500 focus:outline-none'
                    />
                  </div>
                </div>

                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>
                    Company Name
                  </label>
                  <div className='relative'>
                    <Building2 className='absolute left-3 top-3 h-5 w-5 text-gray-400' />
                    <input
                      type='text'
                      placeholder='Your company name'
                      className='w-full rounded-lg border-2 border-gray-300 py-3 pl-10 pr-4 focus:border-orange-500 focus:outline-none'
                    />
                  </div>
                </div>

                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>
                    Message
                  </label>
                  <textarea
                    placeholder='Tell us about your requirements...'
                    rows={4}
                    className='w-full rounded-lg border-2 border-gray-300 p-3 focus:border-orange-500 focus:outline-none'
                  />
                </div>
              </form>

              {/* Premium Features */}
              <div className='mt-6'>
                <h3 className='mb-3 text-sm font-semibold text-gray-900'>
                  Premium Benefits
                </h3>
                <ul className='space-y-2'>
                  {[...(selectedPlan?.features?.items ?? [])]
                    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                    .map(service => (
                      <li
                        key={service.item}
                        className='flex items-start space-x-2'
                      >
                        <div
                          style={{
                            backgroundColor: selectedPlan?.colors?.primary
                          }}
                          className='mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full'
                        >
                          <Check className='h-3 w-3 text-white' />
                        </div>
                        <span className='text-sm text-gray-700'>
                          {service.item}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div
              className='sticky bottom-0  border-t bg-white p-6'
              style={{ borderColor: selectedPlan?.colors?.primary }}
            >
              <button
                style={{
                  backgroundColor: selectedPlan?.button?.backgroundColor,
                  color: selectedPlan?.button?.textColor
                }}
                className='w-full rounded-xl px-6 py-4 font-semibold text-white transition-all duration-200 hover:opacity-90'
              >
                Submit Inquiry
              </button>
              <p className='mt-3 text-center text-xs text-gray-600'>
                Our team will get back to you within 24 hours
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default PricingPlans;
